import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';

import {
  BOOKING_PROCESS_NAME,
  DEAL_PROCESS_NAME,
  DEAL_PURCHASE_PROCESS_NAME,
  PURCHASE_PROCESS_NAME,
  getProcess,
  isBookingProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import {
  onAddAnalytics,
  onSendImageMessage,
  onSendNotification,
  onSendPushNotification,
  onUpdateTransactionMetadata,
  sendWhatsAppMessage,
  transactionLineItems
} from '../../util/api';
import {
  denormalisedEntities,
  denormalisedResponseEntities,
  updatedEntities,
} from '../../util/data';
import { findNextBoundary, getStartOf, monthIdString } from '../../util/dates';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import * as log from '../../util/log';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';

import moment from 'moment';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, fetchCurrentUserNotifications } from '../../ducks/user.duck';
import { SERVICE_LISTING_TYPE, oneSignalMerchantNotificationTemplets } from '../../util/types';
import { parse } from '../../util/urlHelpers';
import {
  getImageUrlFromListingResponse,
  resolveBookedListingId,
} from '../../components/CompleteOrder/completeOrderHelpers';
import { queryCartListings } from '../CartPage/CartPage.duck';
import { trackEventsMixpanel } from '../../mixpanel/mixpanel';
import { Events, mixPanelEventsData, mixPanelEventsScreens } from '../../mixpanel';
import { buildTransactionValueProps } from '../../mixpanel/transactionProps';

const { UUID } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const REVIEW_TX_INCLUDES = ['reviews', 'reviews.author', 'reviews.subject'];

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/TransactionPage/SET_INITIAL_VALUES';

export const FETCH_TRANSACTION_REQUEST = 'app/TransactionPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/TransactionPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/TransactionPage/FETCH_TRANSACTION_ERROR';

export const FETCH_TRANSITIONS_REQUEST = 'app/TransactionPage/FETCH_TRANSITIONS_REQUEST';
export const FETCH_TRANSITIONS_SUCCESS = 'app/TransactionPage/FETCH_TRANSITIONS_SUCCESS';
export const FETCH_TRANSITIONS_ERROR = 'app/TransactionPage/FETCH_TRANSITIONS_ERROR';

export const TRANSITION_REQUEST = 'app/TransactionPage/MARK_RECEIVED_REQUEST';
export const TRANSITION_SUCCESS = 'app/TransactionPage/TRANSITION_SUCCESS';
export const TRANSITION_ERROR = 'app/TransactionPage/TRANSITION_ERROR';

export const FETCH_MESSAGES_REQUEST = 'app/TransactionPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/TransactionPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/TransactionPage/FETCH_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/TransactionPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/TransactionPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/TransactionPage/SEND_MESSAGE_ERROR';

export const SEND_REVIEW_REQUEST = 'app/TransactionPage/SEND_REVIEW_REQUEST';
export const SEND_REVIEW_SUCCESS = 'app/TransactionPage/SEND_REVIEW_SUCCESS';
export const SEND_REVIEW_ERROR = 'app/TransactionPage/SEND_REVIEW_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/TransactionPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/TransactionPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/TransactionPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/TransactionPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/TransactionPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/TransactionPage/FETCH_LINE_ITEMS_ERROR';

export const FETCH_TRANSACTIONS_REQUEST = 'app/OrderCompletePage/FETCH_TRANSACTIONS_REQUEST';
export const FETCH_TRANSACTIONS_SUCCESS = 'app/OrderCompletePage/FETCH_TRANSACTIONS_SUCCESS';
export const FETCH_TRANSACTIONS_ERROR = 'app/OrderCompletePage/FETCH_TRANSACTIONS_ERROR';
export const SET_BOOKED_LISTING_PREVIEW = 'app/OrderCompletePage/SET_BOOKED_LISTING_PREVIEW';
export const SET_CHILD_TRANSACTIONS_REF = 'app/OrderCompletePage/SET_CHILD_TRANSACTIONS_REF';

// ================ Reducer ================ //

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  transitionInProgress: null,
  transitionError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  transactionsRef: null,
  fetchTxsInProgress: false,
  fetchTxsError: null,
  bookedListingPreview: null,
  childTransactionsRef: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: : { uuid: 2 } }, { id: : { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: : { uuid: 2 } }, { id: : { uuid: 1 } }]
const mergeEntityArrays = (a, b) => {
  return a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

export default function transactionPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };
    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }
    case FETCH_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case FETCH_TRANSITIONS_REQUEST:
      return { ...state, fetchTransitionsInProgress: true, fetchTransitionsError: null };
    case FETCH_TRANSITIONS_SUCCESS:
      return { ...state, fetchTransitionsInProgress: false, processTransitions: payload };
    case FETCH_TRANSITIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransitionsInProgress: false, fetchTransitionsError: payload };

    case TRANSITION_REQUEST:
      return {
        ...state,
        transitionInProgress: payload,
        transitionError: null,
      };
    case TRANSITION_SUCCESS:
      return { ...state, transitionInProgress: null };
    case TRANSITION_ERROR:
      return {
        ...state,
        transitionInProgress: null,
        transitionError: payload,
      };

    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, fetchMessagesError: null };
    case FETCH_MESSAGES_SUCCESS: {
      const oldestMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchMessagesInProgress: false,
        messages: mergeEntityArrays(state.messages, payload.messages),
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched,
      };
    }
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };

    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        sendMessageInProgress: true,
        sendMessageError: null,
        initialMessageFailedToTransaction: null,
      };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    case SEND_REVIEW_REQUEST:
      return { ...state, sendReviewInProgress: true, sendReviewError: null };
    case SEND_REVIEW_SUCCESS:
      return { ...state, sendReviewInProgress: false };
    case SEND_REVIEW_ERROR:
      return { ...state, sendReviewInProgress: false, sendReviewError: payload };

    case FETCH_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    // Fetch transactions
    case FETCH_TRANSACTIONS_REQUEST:
      return { ...state, fetchTxsInProgress: true, fetchTxsError: null, transactionsRef: [] };
    case FETCH_TRANSACTIONS_SUCCESS: {
      const results = payload?.results || payload;
      const transactionsRef =
        results?.map(result => ({
          id: result?.data?.data?.id,
          type: 'transaction',
        })) || [];
      return {
        ...state,
        fetchTxsInProgress: false,
        transactionsRef,
        bookedListingPreview: payload?.bookedListingPreview || null,
        childTransactionsRef: payload?.childTransactionsRef || null,
      };
    }
    case FETCH_TRANSACTIONS_ERROR:
      return { ...state, fetchTxsInProgress: false, fetchTxsError: payload };

    case SET_BOOKED_LISTING_PREVIEW:
      return { ...state, bookedListingPreview: payload };

    case SET_CHILD_TRANSACTIONS_REF:
      return { ...state, childTransactionsRef: payload };

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const transitionInProgress = state => {
  return state.TransactionPage.transitionInProgress;
};

// ================ Action creators ================ //
export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchTransactionRequest = () => ({ type: FETCH_TRANSACTION_REQUEST });
const fetchTransactionSuccess = response => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
});
const fetchTransactionError = e => ({ type: FETCH_TRANSACTION_ERROR, error: true, payload: e });

const fetchTransitionsRequest = () => ({ type: FETCH_TRANSITIONS_REQUEST });
const fetchTransitionsSuccess = response => ({
  type: FETCH_TRANSITIONS_SUCCESS,
  payload: response,
});
const fetchTransitionsError = e => ({ type: FETCH_TRANSITIONS_ERROR, error: true, payload: e });

const transitionRequest = transitionName => ({ type: TRANSITION_REQUEST, payload: transitionName });
const transitionSuccess = () => ({ type: TRANSITION_SUCCESS });
const transitionError = e => ({ type: TRANSITION_ERROR, error: true, payload: e });

const fetchMessagesRequest = () => ({ type: FETCH_MESSAGES_REQUEST });
const fetchMessagesSuccess = (messages, pagination) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, ...pagination },
});
const fetchMessagesError = e => ({ type: FETCH_MESSAGES_ERROR, error: true, payload: e });

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: SEND_MESSAGE_SUCCESS });
const sendMessageError = e => ({ type: SEND_MESSAGE_ERROR, error: true, payload: e });

const sendReviewRequest = () => ({ type: SEND_REVIEW_REQUEST });
const sendReviewSuccess = () => ({ type: SEND_REVIEW_SUCCESS });
const sendReviewError = e => ({ type: SEND_REVIEW_ERROR, error: true, payload: e });

export const fetchTimeSlotsRequest = monthId => ({
  type: FETCH_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchTimeSlotsError = (monthId, error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

// For transactions
export const fetchTransactionsRequest = () => ({ type: FETCH_TRANSACTIONS_REQUEST });
export const fetchTransactionsSuccess = payload => ({
  type: FETCH_TRANSACTIONS_SUCCESS,
  payload,
});
export const fetchTransactionsError = error => ({
  type: FETCH_TRANSACTIONS_ERROR,
  error: true,
  payload: error,
});

export const setBookedListingPreview = preview => ({
  type: SET_BOOKED_LISTING_PREVIEW,
  payload: preview,
});

export const setChildTransactionsRef = childTransactionsRef => ({
  type: SET_CHILD_TRANSACTIONS_REF,
  payload: childTransactionsRef,
});

// ================ Thunks ================ //

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone) => (dispatch, getState, sdk) => {
  const monthId = monthIdString(start, timeZone);

  dispatch(fetchTimeSlotsRequest(monthId));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = {
    perPage: 500,
    page: 1,
  };

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(timeSlots => {
      dispatch(fetchTimeSlotsSuccess(monthId, timeSlots));
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(monthId, storableError(e)));
    });
};

function generateUniqueId(length = 10) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let uniqueId = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    uniqueId += characters[randomIndex];
  }
  return uniqueId;
}

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const attributes = listing.attributes;
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone =
    attributes && attributes.availabilityPlan && attributes.availabilityPlan.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && hasTimeZone) {
    const tz = listing.attributes.availabilityPlan.timezone;
    const nextBoundary = findNextBoundary(new Date(), 'hour', tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonth, tz)),
      dispatch(fetchTimeSlots(listing.id, nextMonth, nextAfterNextMonth, tz)),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

// Helper to fetch correct image variants for different thunk calls
const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      // Profile images
      'variants.square-small',
      'variants.square-small2x',

      // Listing images:
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const getNotificationImageQueryParams = () => {
  return {
    'fields.image': [
      'variants.default',
      'variants.square-small',
      'variants.square-small2x',
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.listing-card',
      'variants.listing-card-2x',
    ],
    ...createImageVariantConfig('listing-card', 400, 1),
    ...createImageVariantConfig('listing-card-2x', 800, 1),
  };
};

const listingRelationship = txResponse => {
  return txResponse.data.data.relationships.listing.data;
};

export const fetchTransaction = (id, txRole, config) => (dispatch, getState, sdk) => {
  dispatch(fetchTransactionRequest());
  let txResponse = null;

  return sdk.transactions
    .show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.currentStock',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        ...getImageVariants(config.layout.listingImage),
      },
      { expand: true }
    )
    .then(response => {
      txResponse = response;
      const listingId = listingRelationship(response).id;
      const entities = updatedEntities({}, response.data);
      const listingRef = { id: listingId, type: 'listing' };
      const transactionRef = { id, type: 'transaction' };
      const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
      const listing = denormalised[0];
      const transaction = denormalised[1];
      const processName = resolveLatestProcessName(transaction.attributes.processName);
      try {
        const process = getProcess(processName);
        const isInquiry = process.getState(transaction) === process.states.INQUIRY;

        // Fetch time slots for transactions that are in inquired state
        const canFetchTimeslots =
          txRole === 'customer' && isBookingProcess(processName) && isInquiry;

        if (canFetchTimeslots) {
          fetchMonthlyTimeSlots(dispatch, listing);
        }
      } catch (error) {
        console.log(`transaction process (${processName}) was not recognized`);
      }

      const canFetchListing = listing && listing.attributes && !listing.attributes.deleted;
      if (canFetchListing) {
        return sdk.listings.show({
          id: listingId,
          include: ['author', 'author.profileImage', 'images'],
          ...getImageVariants(config.layout.listingImage),
        });
      } else {
        return response;
      }
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(txResponse, sanitizeConfig));
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchTransactionSuccess(txResponse));
      return response;
    })
    .catch(e => {
      dispatch(fetchTransactionError(storableError(e)));
      throw e;
    });
};

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));
const refreshTx = (sdk, txId) => sdk.transactions.show({ id: txId }, { expand: true });
const refreshTransactionEntity = (sdk, txId, dispatch) => {
  delay(3000)
    .then(() => refreshTx(sdk, txId))
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const lastTransition = response?.data?.data?.attributes?.lastTransition;
      // We'll make another attempt if mark-received-from-purchased from default-purchase process is still the latest.
      if (lastTransition === 'transition/mark-received-from-purchased') {
        return delay(8000)
          .then(() => refreshTx(sdk, txId))
          .then(response => {
            dispatch(addMarketplaceEntities(response));
          });
      }
    })
    .catch(e => {
      // refresh failed, but we don't act upon it.
      console.log('error', e);
    });
};

export const makeTransition = (txId, transitionName, params) => (dispatch, getState, sdk) => {
  if (transitionInProgress(getState())) {
    return Promise.reject(new Error('Transition already in progress'));
  }
  dispatch(transitionRequest(transitionName));

  return sdk.transactions
    .transition({ id: txId, transition: transitionName, params }, { expand: true })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(transitionSuccess());
      dispatch(fetchCurrentUserNotifications());

      // There could be automatic transitions after this transition
      // For example mark-received-from-purchased > auto-complete.
      // Here, we make 1-2 delayed updates for the tx entity.
      // This way "leave a review" link should show up for the customer.
      refreshTransactionEntity(sdk, txId, dispatch);

      return response;
    })
    .catch(e => {
      dispatch(transitionError(storableError(e)));
      log.error(e, `${transitionName}-failed`, {
        txId,
        transition: transitionName,
      });
      throw e;
    });
};

const fetchMessages = (txId, page, config) => (dispatch, getState, sdk) => {
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };
  dispatch(fetchMessagesRequest());

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...getImageVariants(config.layout.listingImage),
      ...paging,
    })
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };
      const totalMessages = getState().TransactionPage.totalMessages;

      // Original fetchMessages call succeeded
      dispatch(fetchMessagesSuccess(messages, pagination));

      // Check if totalItems has changed between fetched pagination pages
      // if totalItems has changed, fetch first page again to include new incoming messages.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      if (totalItems > totalMessages && page > 1) {
        dispatch(fetchMessages(txId, 1, config))
          .then(() => {
            // Original fetch was enough as a response for user action,
            // this just includes new incoming messages
          })
          .catch(() => {
            // Background update, no need to to do anything atm.
          });
      }
    })
    .catch(e => {
      dispatch(fetchMessagesError(storableError(e)));
      throw e;
    });
};

export const fetchMoreMessages = (txId, config) => (dispatch, getState, sdk) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.TransactionPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;

  // In case there're no more old pages left we default to fetching the current cursor position
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessages(txId, nextPage, config));
};

export const sendMessage = (txId, message, config) => (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  return sdk.messages
    .send({ transactionId: txId, content: message })
    .then(response => {
      const messageId = response.data.data.id;

      // We fetch the first page again to add sent message to the page data
      // and update possible incoming messages too.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      return dispatch(fetchMessages(txId, 1, config))
        .then(() => {
          dispatch(sendMessageSuccess());
          return messageId;
        })
        .catch(() => dispatch(sendMessageSuccess()));
    })
    .catch(e => {
      dispatch(sendMessageError(storableError(e)));
      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has already sent a review, we need to make transition to
// transitions.REVIEW_2_BY_<CUSTOMER/PROVIDER>
const sendReviewAsSecond = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch(e => {
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has not yet sent a review, we need to make transition to
// transitions.REVIEW_1_BY_<CUSTOMER/PROVIDER>
// However, the other party might have made the review after previous data synch point.
// So, error is likely to happen and then we must try another state transition
// by calling sendReviewAsSecond().
const sendReviewAsFirst = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch(e => {
      // If transaction transition is invalid, lets try another endpoint.
      if (isTransactionsTransitionInvalidTransition(e)) {
        return sendReviewAsSecond(id, params, role, dispatch, sdk);
      } else {
        dispatch(sendReviewError(storableError(e)));

        // Rethrow so the page can track whether the sending failed, and
        // keep the message in the form for a retry.
        throw e;
      }
    });
};

export const sendReview = (tx, transitionOptionsInfo, params, config) => (
  dispatch,
  getState,
  sdk
) => {
  const { reviewAsFirst, reviewAsSecond, hasOtherPartyReviewedFirst } = transitionOptionsInfo;
  dispatch(sendReviewRequest());

  return hasOtherPartyReviewedFirst
    ? sendReviewAsSecond(tx?.id, reviewAsSecond, params, dispatch, sdk, config)
    : sendReviewAsFirst(tx?.id, reviewAsFirst, params, dispatch, sdk, config);
};

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

export const fetchNextTransitions = id => (dispatch, getState, sdk) => {
  dispatch(fetchTransitionsRequest());

  return sdk.processTransitions
    .query({ transactionId: id })
    .then(res => {
      dispatch(fetchTransitionsSuccess(res.data.data));
    })
    .catch(e => {
      dispatch(fetchTransitionsError(storableError(e)));
    });
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
      });
    });
};

const getFirstImageUrl = (images = [], variant = 'default') => {
  if (!Array.isArray(images) || images.length === 0) {
    return '';
  }

  const variantPriority = [
    variant,
    'listing-card',
    'listing-card-2x',
    'scaled-medium',
    'scaled-small',
    'square-small',
  ];

  for (const image of images) {
    const directUrl = image?.url || image?.attributes?.url || image?.attributes?.originalUrl;
    if (typeof directUrl === 'string' && directUrl.trim().length > 0) {
      return directUrl;
    }

    const variants = image?.attributes?.variants || {};
    for (const variantKey of variantPriority) {
      const variantUrl = variants?.[variantKey]?.url;
      if (typeof variantUrl === 'string' && variantUrl.trim().length > 0) {
        return variantUrl;
      }
    }

    const firstVariantUrl = Object.values(variants).find(entry => entry?.url)?.url;
    if (typeof firstVariantUrl === 'string' && firstVariantUrl.trim().length > 0) {
      return firstVariantUrl;
    }
  }

  return '';
};

const getImageUrlFromIncludedListing = (listing, included = []) => {
  const imageRefs = listing?.relationships?.images?.data;
  if (!Array.isArray(imageRefs) || imageRefs.length === 0) {
    return '';
  }

  const firstImageRef = imageRefs[0];
  const firstImageRefId = firstImageRef?.id?.uuid || firstImageRef?.id || null;
  const firstImage = included.find(
    item => item?.type === 'image' && (item?.id?.uuid || item?.id) === firstImageRefId
  );

  const directUrl =
    firstImage?.url || firstImage?.attributes?.url || firstImage?.attributes?.originalUrl;
  if (typeof directUrl === 'string' && directUrl.trim().length > 0) {
    return directUrl;
  }

  const variants = firstImage?.attributes?.variants || {};
  return (
    variants?.default?.url ||
    variants?.['listing-card']?.url ||
    variants?.['listing-card-2x']?.url ||
    variants?.['scaled-medium']?.url ||
    variants?.['scaled-small']?.url ||
    variants?.['square-small']?.url ||
    Object.values(variants).find(entry => entry?.url)?.url ||
    ''
  );
};

// Helper function to extract commission amount from lineItems
const getCommissionAmount = (lineItems = []) => {
  const providerCommissionItem = lineItems.find(item => item.code === 'line-item/provider-commission');
  if (providerCommissionItem && providerCommissionItem.lineTotal) {
    // Return absolute value of commission amount
    return Math.abs(providerCommissionItem.lineTotal.amount);
  }
  return 0;
};

export const fetchTransactionsAndSendNotification = ({ transactionIds = [] }) => async (
  dispatch,
  getState,
  sdk
) => {
  if (!transactionIds.length) {
    console.warn('No transaction IDs provided.');
    return { success: false };
  }

  const state = getState();
  const currentUser = state?.user?.currentUser;
  if (!currentUser) {
    console.error('Current user not found in state');
    return { success: false };
  }

  const currentUserId = currentUser?.id?.uuid;
  const { displayName, protectedData } = currentUser?.attributes?.profile || {};
  const currentUserDisplayName = displayName;
  const { whatsapp_phone_number = '' } = protectedData ?? {};

  for (const transactionId of transactionIds) {
    let isNotificationSentSuccessfully = true;
    try {
      const queryParams = {
        include: ['provider', 'listing', 'listing.images'],
        ...getNotificationImageQueryParams(),
      };

      const response = await sdk.transactions.show({ id: transactionId, ...queryParams });
      const entities = denormalisedResponseEntities(response);

      if (!Array.isArray(entities) || !entities[0]) {
        console.warn(
          `Invalid response or missing order for transaction ${transactionId}:`,
          response
        );
        continue;
      }

      const order = entities[0];
      const provider = order?.provider;
      const listing = order?.listing;
      const processName = order?.attributes?.processName;
      const protectedData = order?.attributes?.protectedData || {};
      const { address, serviceId } = protectedData;

      if (!provider || !listing) {
        console.warn(`Missing provider or listing data for transaction ${transactionId}:`, order);
        continue;
      }

      // Sending push notification
      let notificationImageUrl = '';
      let notificationItemName = listing?.attributes?.title;
      try {
        const included = response?.data?.included || [];
        const listingId = listing?.id?.uuid || listing?.id || null;

        if (serviceId) {
          try {
            const serviceResponse = await sdk.listings.show(
              {
                id: serviceId,
                include: ['images'],
                ...getNotificationImageQueryParams(),
              },
              { expand: true }
            );
            const serviceListing = serviceResponse?.data?.data;
            const serviceIncluded = serviceResponse?.data?.included || [];
            notificationImageUrl =
              getImageUrlFromIncludedListing(serviceListing, serviceIncluded) ||
              (serviceListing?.images?.length > 0
                ? getFirstImageUrl(serviceListing.images, 'default')
                : '');
            notificationItemName = serviceListing?.attributes?.title || notificationItemName;
          } catch (serviceError) {
            // Non-fatal: fall back to the default listing image/name below.
          }
        }

        const includedListing =
          included.find(
            item =>
              item?.type === 'listing' &&
              (item?.id?.uuid || item?.id) &&
              (item?.id?.uuid || item?.id) === listingId
          ) || included.find(item => item?.type === 'listing');
        if (!notificationImageUrl) {
          notificationImageUrl =
            getImageUrlFromIncludedListing(includedListing, included) ||
            (listing?.images?.length > 0 ? getFirstImageUrl(listing?.images, 'default') : '');
        }

        const customData = {
          image: notificationImageUrl || '',
          orderType: isBookingProcess(processName) ? 'Booking' : 'Order',
          senderName: currentUserDisplayName,
          transactionId: transactionId,
          // orderType: isServiceBooking ? 'Booking' : 'Order',
          itemName: notificationItemName,
        }
        const notificationParams = {
          userRole: 'customer',
          receiverUserIds: provider?.id?.uuid ? [provider.id.uuid] : [],
          senderUserId: currentUserId,
          template_id: oneSignalMerchantNotificationTemplets.ORDER_PLACED,
          notificationData: customData,
          customData: customData
        }
        await onSendPushNotification(notificationParams);
      } catch (error) {
        console.error('[Notification][ORDER_PLACED] push notification FAILED', { error: error?.message });
        isNotificationSentSuccessfully = false;
      }

      // Sending WhatsApp message
      try {
        await sendWhatsAppMessage({
          templateName: isBookingProcess(processName) ? 'service_book' : 'order_place',
          phoneNumber: whatsapp_phone_number,
          language: 'en_GB',
          parameters: [currentUserDisplayName, listing?.attributes?.title],
          buttonParams: [transactionId],
          buttonCount: 1,
        });
      } catch (error) {
        console.error('Error sending WhatsApp message:', error);
        isNotificationSentSuccessfully = false; // Mark as failure
      }

      // If all notifications are sent successfully, update the transaction metadata
      if (isNotificationSentSuccessfully) {
        await onUpdateTransactionMetadata({
          transactionId,
          metadata: { isNotificationSent: true },
        });
      }
    } catch (error) {
      console.error(`Error fetching transaction ${transactionId}:`, error);
    }
  }

  return { success: true };
};

/** Client fallback when deal child transactions were not loaded after loadData. */
export const ensureDealChildTransactionsLoaded = (parentTx, config) => (
  dispatch,
  getState,
  sdk
) => {
  const dealChildIds = parentTx?.attributes?.protectedData?.dealChildTransactions || [];
  if (!dealChildIds.length) {
    return Promise.resolve([]);
  }

  const listingFields = config?.listing?.listingFields;
  const sanitizeConfig = { listingFields };

  const refs = dealChildIds.map(id => ({
    id: new UUID(typeof id === 'string' ? id : id?.uuid),
    type: 'transaction',
  }));
  const existing = getMarketplaceEntities(getState(), refs);
  if (existing.length === dealChildIds.length) {
    return Promise.resolve(existing);
  }

  return Promise.all(
    dealChildIds.map(childTxId =>
      sdk.transactions
        .show({
          id: typeof childTxId === 'string' ? childTxId : childTxId?.uuid,
          include: ['listing', 'listing.images', 'booking'],
          ...getImageVariants(config.layout.listingImage),
        })
        .then(res => {
          dispatch(addMarketplaceEntities(res, sanitizeConfig));
          return res;
        })
        .catch(() => null)
    )
  ).then(results => {
    const childTransactionsRef = results
      .filter(Boolean)
      .map(res => ({
        id: res?.data?.data?.id,
        type: 'transaction',
      }));

    if (childTransactionsRef.length > 0) {
      dispatch(setChildTransactionsRef(childTransactionsRef));
    }

    return getMarketplaceEntities(
      getState(),
      childTransactionsRef.length ? childTransactionsRef : refs
    );
  });
};

/** Client fallback when service listing was not in the store after loadData. */
export const ensureServiceListingLoaded = (serviceId, config) => (dispatch, getState, sdk) => {
  if (!serviceId) {
    return Promise.resolve();
  }

  const listingFields = config?.listing?.listingFields;
  const sanitizeConfig = { listingFields };
  const ref = { id: new UUID(serviceId), type: 'listing' };
  const existing = getMarketplaceEntities(getState(), [ref]);
  const existingListing = existing.length === 1 ? existing[0] : null;
  const hasImages = existingListing?.images?.length > 0;

  if (existingListing && hasImages) {
    return Promise.resolve(existingListing);
  }

  return sdk.listings
    .show({
      id: serviceId,
      include: ['images'],
      ...getImageVariants(config.layout.listingImage),
    })
    .then(res => {
      dispatch(addMarketplaceEntities(res, sanitizeConfig));
      const listing = res?.data?.data;
      const imageUrl = getImageUrlFromListingResponse(listing, res?.data?.included || []);
      if (imageUrl || listing?.attributes?.title) {
        dispatch(
          setBookedListingPreview({
            listingId: serviceId,
            title: listing?.attributes?.title || '',
            imageUrl: imageUrl || '',
          })
        );
      }
      return res;
    })
    .catch(() => null);
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const searchParams = new URLSearchParams(search);
  const idsString = searchParams.get('ids');
  const whatsappTransactionId = searchParams.get('whatsappTransactionId');

  // Decode and split into an array
  const orderItemsTxIds = idsString ? decodeURIComponent(idsString).split(',') : [];

  if (orderItemsTxIds.length) {
    dispatch(fetchTransactionsRequest());

    try {
      // Fetch transactions and process them in parallel
      const results = await Promise.all(
        orderItemsTxIds.map(itemTxId =>
          sdk.transactions
            .show({
              id: itemTxId,
              include: ['listing', 'listing.images', 'provider', 'customer', 'booking'],
              ...getImageVariants(config.layout.listingImage),
            })
            .then(res => {
              const listingFields = config?.listing?.listingFields;
              const sanitizeConfig = { listingFields };
              dispatch(addMarketplaceEntities(res, sanitizeConfig)); // Add entities to the store
              return res;
            })
        )
      );

      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      const bookedListingIds = [
        ...new Set(
          results.map(r => resolveBookedListingId(r?.data?.data)).filter(Boolean)
        ),
      ];

      let bookedListingPreview = null;

      const childTxIds = [
        ...new Set(
          results.flatMap(r => {
            const dealChildTransactions =
              r?.data?.data?.attributes?.protectedData?.dealChildTransactions || [];
            return dealChildTransactions
              .map(id => (typeof id === 'string' ? id : id?.uuid || null))
              .filter(Boolean);
          })
        ),
      ];

      let childTransactionsRef = null;

      if (childTxIds.length > 0) {
        const childResults = await Promise.all(
          childTxIds.map(childTxId =>
            sdk.transactions
              .show({
                id: childTxId,
                include: ['listing', 'listing.images', 'booking'],
                ...getImageVariants(config.layout.listingImage),
              })
              .then(childRes => {
                dispatch(addMarketplaceEntities(childRes, sanitizeConfig));
                return childRes;
              })
              .catch(err => {
                if (process.env.NODE_ENV !== 'production') {
                  console.warn('[OrderCompletePage] deal child tx fetch failed', childTxId, err);
                }
                return null;
              })
          )
        );

        childTransactionsRef = childResults
          .filter(Boolean)
          .map(childRes => ({
            id: childRes?.data?.data?.id,
            type: 'transaction',
          }));
      }

      await Promise.all(
        bookedListingIds.map(listingId =>
          sdk.listings
            .show({
              id: listingId,
              include: ['images'],
              ...getImageVariants(config.layout.listingImage),
            })
            .then(listingRes => {
              dispatch(addMarketplaceEntities(listingRes, sanitizeConfig));
              const listing = listingRes?.data?.data;
              const imageUrl = getImageUrlFromListingResponse(
                listing,
                listingRes?.data?.included || []
              );
              bookedListingPreview = {
                listingId,
                title: listing?.attributes?.title || '',
                imageUrl: imageUrl || '',
                listingType: listing?.attributes?.publicData?.listingType || '',
              };
            })
            .catch(err => {
              if (process.env.NODE_ENV !== 'production') {
                console.warn('[OrderCompletePage] booked listing fetch failed', listingId, err);
              }
            })
        )
      );

      dispatch(
        fetchTransactionsSuccess({
          results,
          bookedListingPreview,
          childTransactionsRef,
        })
      );

      const transaction = results?.[0]?.data?.data || {};
      const { processName } = transaction?.attributes;
      const isNotificationSent = transaction?.attributes?.metadata?.isNotificationSent;

      const currentUser = await dispatch(fetchCurrentUser()); // Fetch current user

      // The listing attached to the transaction (relationships.listing) is
      // looked up here for buildTransactionValueProps, which resolves the
      // staff-booking case (relationships.listing is the staff member's own
      // listing, not the real service) internally -- see transactionProps.js.
      const included = results?.[0]?.data?.included || [];
      const listingEntity = included.find(
        inc => inc?.type === 'listing' && inc?.id?.uuid === transaction?.relationships?.listing?.data?.id?.uuid
      );
      const payInTotal = transaction?.attributes?.payinTotal?.amount;
      const lineItems = transaction?.attributes?.lineItems || [];
      const commissionAmount = getCommissionAmount(lineItems);


      if (!isNotificationSent) {
        // Send notifications after successful transaction fetch
        await dispatch(fetchTransactionsAndSendNotification({ transactionIds: orderItemsTxIds }));

        // The two purchase processes used to fall into neither branch, so
        // product purchases emitted nothing at all. Deal-vs-listing is the
        // only distinction the events make, so the four processes collapse
        // to two.
        const isDealProcess = processName === DEAL_PROCESS_NAME || processName === DEAL_PURCHASE_PROCESS_NAME;
        const isKnownProcess =
          isDealProcess || processName === BOOKING_PROCESS_NAME || processName === PURCHASE_PROCESS_NAME;

        if (isKnownProcess) {
          // bookedListingPreview.title (fetched above from the real listing
          // id) covers the staff-booking case; buildTransactionValueProps
          // uses it only when it actually resolved a different listing.
          const moneyProps = buildTransactionValueProps(transaction, {
            role: 'customer',
            listing: listingEntity,
            listingTitle: bookedListingPreview?.title,
          });

          trackEventsMixpanel(isDealProcess ? Events.CUSTOMER_REDEEM_DEAL : Events.CUSTOMER_BOOKING,
            {
              action_type: isDealProcess ? mixPanelEventsData.DEAL_PURCHASED : mixPanelEventsData.CUSTOMER_BOOKING,
              screen: mixPanelEventsScreens.ORDER_COMPLETE_PAGE,
              user_type: 'customer',
              user_id: currentUser?.id?.uuid,
              // Legacy props, kept only until webadmin's ETL reads the *_sar
              // props below. commission_amount covers line-item/provider-commission
              // alone, so it misses the VAT on the NOW fee and undercounts
              // platform revenue -- use platform_fee_sar instead.
              payin_total: payInTotal != null ? payInTotal / 100 : null,
              commission_amount: commissionAmount / 100,
              ...moneyProps,
              // Deal events use deal_id/deal_title instead of the generic names.
              ...(isDealProcess
                ? { deal_id: moneyProps.listing_id, deal_title: moneyProps.listing_title }
                : {}),
            }
          );
        }
      }

      // Parse the search query and dispatch related cart listings
      const queryParams = parse(search);
      const page = queryParams?.page || 1;

      await dispatch(
        queryCartListings(
          {
            ...queryParams,
            page,
          },
          config,
          null,
          currentUser
        )
      );

      const { whatsapp_phone_number } = currentUser?.attributes?.profile?.protectedData || {};

      // WhatsApp confirmation message — only when the customer has a number on file
      if (whatsapp_phone_number) {
        sendImageMessage({
          transactionIds: orderItemsTxIds,
          to: whatsapp_phone_number,
          whatsappTransactionId,
        });
      }

      // Analytics insert — must run for EVERY completed order, with or without WhatsApp
      {
        const transactionId = transaction?.id?.uuid;
        const { processName, payoutTotal, protectedData } = transaction?.attributes;
        const { amount } = payoutTotal || {};
        const { serviceId, dealChildTransactions, parentTransactionId } = protectedData || {};

        const { customer, provider, listing } = transaction?.relationships || {}
        const customerId = customer?.data?.id?.uuid;
        const providerId = provider?.data?.id?.uuid;
        const listingId = listing?.data?.id?.uuid;
        const analyticsData = {
          transaction_id: transactionId,
          provider_id: providerId,
          process_type: processName,
          status: "PURCHASED",
          amount: amount ? amount / 100 : 0,
          created_at: moment().unix(),
          // Null for every row this page writes -- it only ever reports on
          // results[0], which is always a parent. Sent so that a row is
          // self-describing if any future path does write a child.
          parent_transaction_id: parentTransactionId || null,
        };

        let dealChildTxListingId = "", dealChildListingId = "";

        if (dealChildTransactions?.length > 0 && [DEAL_PURCHASE_PROCESS_NAME, DEAL_PROCESS_NAME].includes(processName)) {
          await sdk.transactions
            .show({
              id: dealChildTransactions[0],
              include: ['listing'],
            })
            .then(res => {
              const listingFields = config?.listing?.listingFields;
              const sanitizeConfig = { listingFields };
              dispatch(addMarketplaceEntities(res, sanitizeConfig));

              const { attributes, relationships } = res?.data?.data || {}
              const { serviceId } = attributes?.protectedData || {};
              const listingId = relationships?.listing?.data?.id?.uuid;

              if (listingId) {
                dealChildTxListingId = listingId;
              }
              if (serviceId) {
                dealChildListingId = serviceId;;
              }
              return res;
            })
        }

        switch (processName) {
          case DEAL_PURCHASE_PROCESS_NAME: {
            analyticsData.listing_id = dealChildTxListingId;
            analyticsData.deal_id = listingId;
            break;
          }
          case DEAL_PROCESS_NAME: {
            analyticsData.listing_id = dealChildListingId;
            analyticsData.staff_id = dealChildTxListingId;
            analyticsData.deal_id = listingId;
            break;
          }
          case BOOKING_PROCESS_NAME: {
            analyticsData.listing_id = serviceId;
            analyticsData.staff_id = listingId;
            break;
          }
          case PURCHASE_PROCESS_NAME: {
            analyticsData.listing_id = listingId;
            break;
          }
        }

        // A child transaction must never get its own analytics row. The staff
        // half of a deal redemption shares ONE payment authorisation with its
        // parent, so counting both reported a 372.60 redemption as 798.10.
        //
        // Today this cannot happen, but only by accident: the block above uses
        // results[0] and the parent always sorts first in `ids`. Reorder that
        // query param, or loop `results` here, and revenue silently doubles.
        // The guard makes the rule explicit and matches the marker
        // now-webadmin's poller already uses (revenueBreakdown.ts isAttachedChild).
        if (parentTransactionId) {
          console.log(`skipping analytics for child transaction ${transactionId} of ${parentTransactionId}`);
        } else {
          onAddAnalytics(analyticsData)
            .then(() => console.log("success"))
            .catch((err) => console.log("error in adding analytics"))
        }
      }
      return;
    } catch (error) {
      // Catch and handle errors in transaction fetch or any async operation
      dispatch(fetchTransactionsError(storableError(error)));
      console.error('Error loading transaction data:', error);
      throw error; // Re-throw error after dispatching error action
    }
  } else {
    console.warn('No transaction IDs provided in the URL.');
    return { success: false }; // If no transaction IDs found, return failure
  }
};

const sendImageMessage = async body => {
  return onSendImageMessage(body)
    .then(response => {
      console.log('Image message sent successfully:', response);
      return;
    })
    .catch(err => {
      console.error('Error sending image message:', err);
    });
};
